import Image from '@react-spectrum/s2/icons/Image';
import {
	iconStyle,
	style,
} from '@react-spectrum/s2/style' with { type: 'macro' };

import { BlobImage } from '@/components/blob/BlobImage';
import type { EffectiveStatus } from '@/hooks/useContentArtifacts';
import { cn } from '@/utils/cn';

import type { DigitalAd } from '../../../../bridge/types/artifact.ts';

import { TileFooter } from './TileFooter';
import { TileSelectionCheckbox } from './TileSelectionCheckbox';

// 'gray', not 'gray-700': iconStyle takes base color names, not scale steps, so the
// old value was not a color the macro could emit — the icon fell back to inheriting.
const adIconStyle = iconStyle({ size: 'M', color: 'gray' });

interface DigitalAdAssetProps {
	artifact: DigitalAd;
	effectiveStatus?: EffectiveStatus;
	onClick?: () => void;
	isSelectable?: boolean;
	isSelected?: boolean;
	onToggleSelect?: () => void;
}

export const DigitalAdAsset = ({
	artifact,
	effectiveStatus,
	onClick,
	isSelectable,
	isSelected,
	onToggleSelect,
}: DigitalAdAssetProps) => {
	const images = artifact.content.image_urls ?? [];
	const status: EffectiveStatus = effectiveStatus ?? artifact.status;
	const headline = artifact.content.headline || 'Untitled ad';

	return (
		<div
			className="group flex cursor-pointer flex-col gap-2"
			data-testid={`content-tile-${artifact.id}`}
			onClick={onClick}
		>
			<div className="relative">
				<BlobImage
					src={images[0]}
					alt={`Digital ad: ${headline}`}
					className={cn(
						'aspect-square h-full w-full rounded-lg object-cover',
						'opacity-90 transition-opacity group-hover:opacity-100',
					)}
					// Without a fallback BlobImage renders nothing at all for an ad with
					// no usable image, collapsing the tile to zero height and leaving its
					// title floating in the middle of the grid row.
					fallback={{}}
				/>
				{isSelectable && onToggleSelect && (
					<TileSelectionCheckbox
						label={headline}
						isSelected={isSelected ?? false}
						onToggle={onToggleSelect}
					/>
				)}
			</div>
			<div className="flex flex-col gap-1">
				<div className="flex justify-between gap-2">
					<div
						className={cn(
							'line-clamp-1 flex-1',
							style({
								font: 'title',
								fontSize: 'title-sm',
								fontWeight: 'bold',
								color: 'title',
							}),
						)}
					>
						{headline}
					</div>
					<Image styles={adIconStyle} />
				</div>
				<TileFooter status={status} date={null} />
			</div>
		</div>
	);
};
